/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CELL_TEXT_PRIMARY_CLASS } from '@gh/common-internal-shared';
import {
  DateTimeTypes,
  FormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
  toAppFormGroup,
} from '@gh/common-shared';
import {
  createEmptySlicedDataSource,
  emptySelectionModel,
  isSelectionModelEmpty,
  SelectionModel,
  SlicedDataSource,
} from '@gh/core-data';
import { NotificationService, WaitingService } from '@gh/core-ui';
import { buildDateTimeColumn, GridDefinitionFactory, LayoutBasedGridDefinition } from '@gh/core-ui-grid';
import { BulkDeleteHpBusinessService } from '../resources/bulk-delete-hp-business.service';
import {
  BULK_DELETE_HP_DEFAULT_FILTER,
  BulkDeleteHpFilter,
  HpBulkDeleteListItem,
} from '../resources/bulk-delete-hp.model';

@Component({
  selector: 'gh-bulk-delete-hp',
  template: require('./bulk-delete-hp.component.html'),
})
export class BulkDeleteHpComponent implements OnInit {
  form: FormState<BulkDeleteHpFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  dataSource: SlicedDataSource<HpBulkDeleteListItem>;
  selectionModel: SelectionModel;

  private filtered = false;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private formStateBuilder: FormStateBuilder,
              private notifications: NotificationService,
              private business: BulkDeleteHpBusinessService,
              private waitings: WaitingService) {
  }

  get isFiltered(): boolean {
    return this.filtered;
  }

  get hasSelected(): boolean {
    return !isSelectionModelEmpty(this.selectionModel);
  }

  ngOnInit(): void {
    const { init, gridLayout } = getRouteInitData(this.route);

    this.gridDefinition = this.buildGridDefinition();
    this.gridDefinition.bind(gridLayout);

    this.form = this.formStateBuilder.buildPlain({
      value: BULK_DELETE_HP_DEFAULT_FILTER,
      children: {
        days: {
          required: true,
          type: NumberTypes.Days,
          validators: [Validators.min(init.deleteHousePlanAfterDays)],
        },
        isQqOnly: void 0,
      },
    });

    this.reset();
  }

  onSearch(): void {
    if (this.form.invalid) {
      return;
    }

    this.dataSource = this.business.findAllAsDataSource(this.form.submitValue);
    this.filtered = true;
    this.enableFilterForm(false);
  }

  onReset(): void {
    this.reset();
  }

  onDelete(): void {
    this.business.execute(this.form.submitValue, this.selectionModel).pipe(
      this.waitings.operator<void>())
      .subscribe(
        () => {
          this.reset();
          this.notifications.success('msg_bulk_update_initiated_successfully');
        },
        this.notifications.processAll,
      );
  }

  onSelectionModelChanged(model: SelectionModel): void {
    this.selectionModel = model;
  }

  private reset(): void {
    this.dataSource = createEmptySlicedDataSource();
    this.selectionModel = emptySelectionModel();
    this.filtered = false;
    this.enableFilterForm(true);
  }

  private enableFilterForm(enabled: boolean): void {
    toAppFormGroup(this.form.group).updateSchema({ enabled });
  }

  private buildGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased(
      {
        code: { cellClass: CELL_TEXT_PRIMARY_CLASS },
        rfqUpdated: buildDateTimeColumn(DateTimeTypes.ShortDate),
        costUpdated: buildDateTimeColumn(DateTimeTypes.ShortDate),
        updatedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        getRowNodeId: (data: HpBulkDeleteListItem) => data.housePlanId,
      });
  }
}
